import { FC, ReactNode, Suspense, lazy, useCallback, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { graphql, useFragment, useQueryLoader } from 'react-relay';
import PdpSharedShippingInfoLoading from './PdpSharedShippingInfoLoading';

import {
    getUserGeoInfo,
    WAIT_FOR_SHIPPING_REGIONS,
} from 'dibs-regional-info/exports/regionalInfoHelpers';
import { useUserShippingRegionsUrlLabels } from '../PdpSharedLowerShippingInfo/hooks/useUserShippingRegionsUrlLabels';
import { getPartialAddressCheckoutEligibility } from 'dibs-checkout/exports/partialAddressesCheckoutAbTestHelpers';
import { getFdfQuoteABTestVariant } from 'dibs-buyer-layout/src/helpers/getFdfQuoteABTestVariant';
import { updateRelayVariables } from '../../actions/sharedRelayVariableActions';

import { PdpSharedShippingInfoWrapper_item$key } from './__generated__/PdpSharedShippingInfoWrapper_item.graphql';
import { PdpSharedShippingInfoWrapper_user$key } from './__generated__/PdpSharedShippingInfoWrapper_user.graphql';

import {
    PdpSharedShippingInfoWrapperQuery as PdpSharedShippingInfoWrapperQueryType,
    PdpSharedShippingInfoWrapperQuery$variables,
} from './__generated__/PdpSharedShippingInfoWrapperQuery.graphql';

const PdpSharedShippingInfoSidebarComponent = lazy(() =>
    import(
        /* webpackChunkName: "PdpSharedShippingInfoSidebarComponent" */ './PdpSharedShippingInfoSidebarComponent'
    ).then(mod => ({ default: mod.PdpSharedShippingInfoSidebarComponent }))
);

const PdpSharedShippingInfoComponent = lazy(() =>
    import(
        /* webpackChunkName: "PdpSharedShippingInfoComponent" */ './PdpSharedShippingInfoComponent'
    ).then(mod => ({ default: mod.PdpSharedShippingInfoComponent }))
);

//In this case we won't satisfy the eslint rule since the child component is using theses fields
/* eslint-disable relay/unused-fields */
export const PdpSharedShippingInfoWrapperQuery = graphql`
    query PdpSharedShippingInfoWrapperQuery(
        $itemId: String!
        $userId: String!
        $fetchRegionalInfo: Boolean = false
        $userZipCode: String = ""
        $userCountryCode: String = ""
        $isTrade: Boolean = false
        $fetchItem: Boolean = false
        $fetchUser: Boolean = false
        $isClient: Boolean = false
        $itemQuantity: Int
        $shippingRegions: [String] = []
        $partialAddressCheckoutEligibility: String!
        $fdfQuoteABTestVariant: String
    ) {
        viewer {
            ...PdpSharedShippingHolidayDelivery_viewer
            ...PdpSharedShippingInfoLink_viewer
            ...PdpSharedPromoCode_viewer
            ...PdpSharedShippingAddress_viewer
            ...PdpSharedLowerShippingInfo_viewer
                @arguments(
                    fetchRegionalInfo: $fetchRegionalInfo
                    userZipCode: $userZipCode
                    userCountryCode: $userCountryCode
                )
            ...useFDFQuoteVariant_viewer
            ...useShippingPrice_viewer
                @arguments(
                    fetchRegionalInfo: $fetchRegionalInfo
                    userZipCode: $userZipCode
                    userCountryCode: $userCountryCode
                )
            ...useShippingType_viewer
                @arguments(
                    userId: $userId
                    itemId: $itemId
                    fetchRegionalInfo: $fetchRegionalInfo
                    userZipCode: $userZipCode
                    userCountryCode: $userCountryCode
                    itemQuantity: $itemQuantity
                    fdfQuoteABTestVariant: $fdfQuoteABTestVariant
                )
            regionSelectorEnabled: featureFlag(feature: "pdpRegionSelector")
            geography {
                countries {
                    name
                    abbreviation
                }
            }
            relevantShipping(
                itemId: $itemId
                buyerId: $userId
                quantity: $itemQuantity
                zipCode: $userZipCode
                countryCode: $userCountryCode
                fdfQuoteABTestVariant: $fdfQuoteABTestVariant
            ) {
                ...useFDFQuoteVariant_relevantShipping
                quoteRequestStatus
                shipmentQuote {
                    ...PdpSharedShippingInfoLink_shipmentQuote
                    ...PdpSharedDutiesMessage_shipmentQuote
                    type
                    handlingTimeDisplay(displayShort: true)
                    subTotalAmount {
                        convertedAmountList {
                            currency
                            amount
                        }
                    }
                    buyerServiceMethodDisplay
                    showQuoteAsPending
                    serviceMethod {
                        serviceType
                        serviceMethodType
                    }
                }
                shipment {
                    shippingAddress {
                        displayCityStateCountry
                    }
                }
            }
            itemRead(itemIds: [$itemId]) @include(if: $fetchItem) {
                ... on Item {
                    ...PdpSharedLowerShippingInfo_item
                        @arguments(
                            isTrade: $isTrade
                            buyerId: $userId
                            fetchShippingCostInfo: true
                            itemQuantity: $itemQuantity
                            fdfQuoteABTestVariant: $fdfQuoteABTestVariant
                            userCountryCode: $userCountryCode
                            userZipCode: $userZipCode
                            shippingRegions: $shippingRegions
                        )
                    ...useFDFQuoteVariant_item
                    ...useShippingPrice_item
                        @arguments(
                            buyerId: $userId
                            fetchShippingCostInfo: true
                            itemQuantity: $itemQuantity
                            fdfQuoteABTestVariant: $fdfQuoteABTestVariant
                            userZipCode: $userZipCode
                            userCountryCode: $userCountryCode
                        )

                    ...useShippingType_item
                        @arguments(
                            userId: $userId
                            itemQuantity: $itemQuantity
                            userZipCode: $userZipCode
                            userCountryCode: $userCountryCode
                            fdfQuoteABTestVariant: $fdfQuoteABTestVariant
                        )
                    ...PdpSharedShippingInfoLink_item
                    ...PdpSharedShippingInfoWrapper_item
                    ...PdpSharedDutiesMessage_item
                    serviceId
                    isItemRTQuoteEligibly(
                        buyerId: $userId
                        countryCode: $userCountryCode
                        zipCode: $userZipCode
                    )
                }
            }
            user(userId: $userId) @include(if: $fetchUser) {
                ...useFDFQuoteVariant_buyer
                ...PdpSharedPromoCode_user
                ...PdpSharedShippingInfoLink_user
                ...PdpSharedShippingAddress_user
                serviceId
                addressBook(partialAddressCheckoutEligibility: $partialAddressCheckoutEligibility) {
                    proAddress {
                        displayCountry
                        country
                        zipCode
                    }
                    personalAddress {
                        displayCountry
                        country
                        zipCode
                    }
                    addressList {
                        displayCountry
                        country
                        zipCode
                    }
                }
            }
        }
    }
`;
/* eslint-disable relay/unused-fields */

type Props = {
    item: PdpSharedShippingInfoWrapper_item$key;
    user: PdpSharedShippingInfoWrapper_user$key | null | undefined;
    currency: string;
    children?: ReactNode;
    leadTime?: string | null;
    renderTooltip?: boolean;
    isClient?: boolean;
    title?: ReactNode;
    displayInline?: boolean;
    preLinkQuoteDetails?: ReactNode;
    postLinkQuoteDetails?: ReactNode;
    postLinkNoQuoteDetails?: ReactNode;
    itemQuantity?: number | null;
    calculateLowerShippingInfo?: boolean;
    positioning?: 'sidebar' | 'seller_details';
    showShippingIcon?: boolean;
    shortLinkCta?: boolean;
    shortRequestQuote?: boolean;
    shippingInfoClass?: string;
    handleRoundPriceTracking?: () => void;
};

const PdpSharedShippingInfoWrapper: FC<Props> = ({
    item: itemRef,
    user: userRef,
    currency,
    children,
    leadTime,
    renderTooltip,
    isClient = false,
    title,
    displayInline,
    preLinkQuoteDetails,
    postLinkQuoteDetails,
    postLinkNoQuoteDetails,
    itemQuantity,
    calculateLowerShippingInfo = false,
    positioning,
    showShippingIcon,
    shippingInfoClass,
    shortLinkCta,
    shortRequestQuote,
    handleRoundPriceTracking,
}) => {
    const [queryReference, loadQuery] = useQueryLoader<PdpSharedShippingInfoWrapperQueryType>(
        PdpSharedShippingInfoWrapperQuery
    );
    const dispatch = useDispatch();
    const shippingRegions = useUserShippingRegionsUrlLabels();

    const user = useFragment(
        graphql`
            fragment PdpSharedShippingInfoWrapper_user on User {
                serviceId
                isVerifiedTrade
            }
        `,
        userRef
    );
    const item = useFragment(
        graphql`
            fragment PdpSharedShippingInfoWrapper_item on Item {
                serviceId
            }
        `,
        itemRef
    );

    const itemId = item?.serviceId;
    const userId = user?.serviceId;
    const isTradeUser = user?.isVerifiedTrade;

    const reloadQuery = useCallback(
        (variables: Partial<PdpSharedShippingInfoWrapperQuery$variables>) => {
            /**
             * Wait for shipping regions to be populated (either an array or undefined)
             * By doing this we avoid unnecessary shipmentQuotes/live calls
             */
            if (shippingRegions !== WAIT_FOR_SHIPPING_REGIONS) {
                const userGeoInfo = getUserGeoInfo();
                loadQuery(
                    {
                        itemId: itemId || '',
                        userId: userId || '',
                        isTrade: isTradeUser,
                        fetchUser: !!userId,
                        fetchItem: !!itemId,
                        isClient,
                        itemQuantity,
                        userCountryCode: userGeoInfo?.countryCode || '',
                        userZipCode: userGeoInfo?.zipCode || '',
                        shippingRegions,
                        partialAddressCheckoutEligibility: getPartialAddressCheckoutEligibility(),
                        fdfQuoteABTestVariant: getFdfQuoteABTestVariant() || '',
                        // We want to override query variables with the new ones when refetching the query
                        ...variables,
                    },
                    { fetchPolicy: 'network-only' }
                );
                dispatch(
                    updateRelayVariables({
                        updateItemPricing: true,
                        updatedUserCountryCode: variables?.userCountryCode,
                    })
                );
            }
        },
        [loadQuery, dispatch, itemId, userId, isTradeUser, isClient, itemQuantity, shippingRegions]
    );

    useEffect(() => {
        /**
         * Wait for shipping regions to be populated (either an array or undefined)
         * By doing this we avoid unnecessary shipmentQuotes/live calls
         */
        if (isClient && shippingRegions !== WAIT_FOR_SHIPPING_REGIONS) {
            const userGeoInfo = getUserGeoInfo();
            loadQuery(
                {
                    itemId: itemId || '',
                    userId: userId || '',
                    isTrade: isTradeUser,
                    fetchUser: !!userId,
                    fetchItem: !!itemId,
                    isClient,
                    userCountryCode: userGeoInfo?.countryCode || '',
                    userZipCode: userGeoInfo?.zipCode || '',
                    itemQuantity,
                    shippingRegions,
                    fetchRegionalInfo: false,
                    partialAddressCheckoutEligibility: getPartialAddressCheckoutEligibility(),
                    fdfQuoteABTestVariant: getFdfQuoteABTestVariant() || '',
                },
                { fetchPolicy: 'network-only' }
            );
        }
    }, [loadQuery, isClient, itemId, userId, isTradeUser, itemQuantity, shippingRegions]);

    if (!queryReference) {
        return (
            <PdpSharedShippingInfoLoading title={title}>{children}</PdpSharedShippingInfoLoading>
        );
    }

    if (positioning === 'sidebar') {
        return (
            <Suspense
                fallback={
                    <PdpSharedShippingInfoLoading title={title}>
                        {children}
                    </PdpSharedShippingInfoLoading>
                }
            >
                <PdpSharedShippingInfoSidebarComponent
                    queryReference={queryReference}
                    reloadQuery={reloadQuery}
                    currency={currency}
                    loadingTitle={title}
                    preLinkQuoteDetails={preLinkQuoteDetails}
                    postLinkQuoteDetails={postLinkQuoteDetails}
                    postLinkNoQuoteDetails={postLinkNoQuoteDetails}
                    itemQuantity={itemQuantity}
                    showShippingIcon={showShippingIcon}
                    shippingInfoClass={shippingInfoClass}
                    shortLinkCta={shortLinkCta}
                    shortRequestQuote={shortRequestQuote}
                    handleRoundPriceTracking={handleRoundPriceTracking}
                >
                    {children}
                </PdpSharedShippingInfoSidebarComponent>
            </Suspense>
        );
    } else {
        return (
            <Suspense
                fallback={
                    <PdpSharedShippingInfoLoading title={title}>
                        {children}
                    </PdpSharedShippingInfoLoading>
                }
            >
                <PdpSharedShippingInfoComponent
                    queryReference={queryReference}
                    reloadQuery={reloadQuery}
                    renderTooltip={renderTooltip}
                    currency={currency}
                    leadTime={leadTime || ''}
                    userId={user?.serviceId || ''}
                    title={title}
                    displayInline={displayInline}
                    preLinkQuoteDetails={preLinkQuoteDetails}
                    postLinkQuoteDetails={postLinkQuoteDetails}
                    postLinkNoQuoteDetails={postLinkNoQuoteDetails}
                    itemQuantity={itemQuantity}
                    calculateLowerShippingInfo={calculateLowerShippingInfo}
                    positioning={positioning}
                    handleRoundPriceTracking={handleRoundPriceTracking}
                >
                    {children}
                </PdpSharedShippingInfoComponent>
            </Suspense>
        );
    }
};

export default PdpSharedShippingInfoWrapper;
